<template>
  <div class="pa-4">
    <v-layout row justify-space-between align-center class="mb-3">
      <div class="headline">{{ title }}</div>
      <v-btn color="success" small @click="newUserDialog = true">
        <v-icon left>mdi-account-plus</v-icon>New User
      </v-btn>
    </v-layout>

    <v-dialog persistent v-model="newUserDialog" max-width="400">
      <v-form ref="newUserForm">
        <v-card>
          <v-card-title class="headline green lighten-4">New User</v-card-title>
          <v-card-text class="pb-0">
            <v-text-field
              v-model="newUser.fullName"
              label="Name"
              :rules="[rules.required]"
            ></v-text-field>
            <v-text-field
              v-model="newUser.email"
              label="Email"
              :rules="[rules.email]"
            ></v-text-field>
            <v-text-field
              v-model="newUser.phoneNumber"
              label="Phone Number"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-card-text>
          <span
            class="ml-3"
            style="color: red; font-size: 12px;"
            v-if="!newUser.fullName || (!newUser.email && !newUser.phoneNumber)"
            >Please fill in name and at least one contact detail</span
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="default" dark small @click="closeNewUserDialog()"
              >Cancel</v-btn
            >
            <v-btn
              color="success"
              small
              :loading="newUser.processing"
              :disabled="
                !newUser.fullName || (!newUser.email && !newUser.phoneNumber)
              "
              @click="saveNewUser()"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <v-text-field
      style="width: 30%;"
      v-if="!loading"
      v-model="search"
      label="Search"
      prepend-icon="mdi-magnify"
      clearable
      class="mb-2"
      hide-details
    ></v-text-field>

    <v-data-table
      v-if="!loading"
      :headers="headers"
      :items="filteredUsers"
      :loading="loading"
      class="elevation-1"
      :rows-per-page-items="[100, 500, 1000]"
      :pagination.sync="pagination"
    >
      <template slot="items" slot-scope="props">
        <tr @click="onRowClick(props.item)" style="cursor: pointer;">
          <td>{{ props.item.fullName }}</td>
          <td>{{ props.item.email }}</td>
          <td>{{ props.item.phoneNumber }}</td>
          <td>{{ props.item.createdBy }}</td>
          <td>{{ formatDate(props.item.createdOn) }}</td>
        </tr>
      </template>
    </v-data-table>

    <div v-else class="text-center">
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
    </div>

    <v-dialog persistent v-model="dialog" max-width="500">
      <v-form ref="editForm">
        <v-card v-if="selectedUser">
          <v-card-title class="headline green lighten-4">
            {{ editMode ? "Edit User" : selectedUser.fullName }}
          </v-card-title>
          <v-card-text class="pb-0">
            <template v-if="!editMode">
              <v-layout row class="mb-2 mt-2">
                <div class="font-weight-medium mr-2">Email:</div>
                <div>{{ selectedUser.email }}</div>
              </v-layout>
              <v-layout row class="mb-2">
                <div class="font-weight-medium mr-2">Phone:</div>
                <div>{{ selectedUser.phoneNumber }}</div>
              </v-layout>
              <v-layout row class="mb-2">
                <div class="font-weight-medium mr-2">Created By:</div>
                <div>{{ selectedUser.createdBy }}</div>
              </v-layout>
              <v-layout row class="mb-2">
                <div class="font-weight-medium mr-2">Created On:</div>
                <div>{{ formatDate(selectedUser.createdOn) }}</div>
              </v-layout>
            </template>
            <template v-else>
              <v-text-field
                label="Full Name"
                v-model="editForm.fullName"
                :rules="[rules.required]"
              ></v-text-field>
              <v-text-field
                label="Email"
                v-model="editForm.email"
                :rules="[rules.email]"
              ></v-text-field>
              <v-text-field
                label="Phone"
                v-model="editForm.phoneNumber"
                :rules="[rules.phone]"
              ></v-text-field>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <template v-if="!editMode">
              <v-btn
                color="default"
                dark
                small
                @click="
                  dialog = false;
                  editMode = false;
                "
                >Close</v-btn
              >
              <v-btn color="success" small @click="startEdit()">Edit</v-btn>
            </template>
            <template v-else>
              <v-btn color="default" dark small @click="editMode = false"
                >Cancel</v-btn
              >
              <v-btn
                color="success"
                small
                :disabled="
                  !editForm.fullName ||
                    (!editForm.email && !editForm.phoneNumber)
                "
                :loading="saving"
                @click="saveUser()"
                >Save</v-btn
              >
            </template>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
export default {
  metaInfo() {
    return {
      title: `${this.title}`,
    };
  },
  name: "ConferenceRoomUsers",
  data() {
    return {
      title: "Conference Room Users",
      users: [],
      loading: false,
      dialog: false,
      selectedUser: null,
      editMode: false,
      editForm: {},
      saving: false,
      search: "",
      pagination: { sortBy: "id", descending: false },
      newUserDialog: false,
      newUser: { fullName: "", email: "", phoneNumber: "", processing: false },
      headers: [
        { text: "Full Name", value: "fullName" },
        { text: "Email", value: "email" },
        { text: "Phone", value: "phoneNumber" },
        { text: "Created By", value: "createdBy" },
        { text: "Created On", value: "createdOn" },
      ],
      rules: {
        required: (value) => !!value || "Required.",
        email: (v) =>
          !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Invalid email address",
        phone: (v) =>
          !v ||
          /^\d{10}$/.test(v) ||
          "Phone number must be a valid US number, 10 digits without dashes or parentheses",
        timeFormat: (v) =>
          !v || /^([01]\d|2[0-3]):([0-5]\d)$/.test(v) || "Invalid time format",
      },
    };
  },
  computed: {
    filteredUsers() {
      if (!this.search) return this.users;
      const s = this.search.toLowerCase();
      return this.users.filter((u) =>
        [u.fullName, u.email, u.phoneNumber].some(
          (v) => v && v.toLowerCase().includes(s)
        )
      );
    },
  },
  async created() {
    await this.loadUsers();
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        this.users = await this.$http.get("api/ConferenceRoomUsers/GetAll");
      } catch (error) {
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleString();
    },
    onRowClick(user) {
      this.selectedUser = user;
      this.dialog = true;
    },
    startEdit() {
      this.editForm = {
        id: this.selectedUser.id,
        fullName: this.selectedUser.fullName,
        email: this.selectedUser.email,
        phoneNumber: this.selectedUser.phoneNumber,
      };
      this.editMode = true;
    },
    async saveUser() {
      if (!this.$refs.editForm.validate(true)) return;
      this.saving = true;
      try {
        await this.$http.post("api/ConferenceRoomUsers/Update", this.editForm);
        this.selectedUser.fullName = this.editForm.fullName;
        this.selectedUser.email = this.editForm.email;
        this.selectedUser.phoneNumber = this.editForm.phoneNumber;
        this.editMode = false;
        this.$notify({
          group: "notification",
          type: "success",
          text: "User updated successfully",
        });
      } catch {
        this.$notify({
          group: "notification",
          type: "error",
          text: "Error updating user",
        });
      }
      this.saving = false;
      this.dialog = false;
    },
    closeNewUserDialog() {
      this.newUserDialog = false;
      this.newUser = {
        fullName: "",
        email: "",
        phoneNumber: "",
        processing: false,
      };
      this.$refs.newUserForm.reset();
    },
    async saveNewUser() {
      if (!this.$refs.newUserForm.validate(true)) return;
      this.newUser.processing = true;
      try {
        await this.$http.post("api/ConferenceRoomUsers/Create", this.newUser);
        this.$notify({
          group: "notification",
          type: "success",
          text: "User created successfully",
        });
        this.closeNewUserDialog();
        await this.loadUsers();
      } catch {
        this.$notify({
          group: "notification",
          type: "error",
          text: "Error creating user",
        });
      }
      this.newUser.processing = false;
    },
  },
};
</script>
